// utils/dateDisplayUtils.js
import moment from 'moment';
import { calendarDaySpan } from './durationSpan.js';

// A job is only "scheduled" once it has been booked (or progressed past
// booking). NB: request_offered carries a *proposed* startDate even for unbooked
// NEW/ANSWERED requests, so the presence of request.scheduledStart is NOT a
// reliable "is scheduled" signal — always gate on currentState.
const SCHEDULED_STATES = ['scheduled', 'received', 'started', 'finished', 'collected'];

export function isJobScheduled(request) {
  return SCHEDULED_STATES.includes(request?.currentState);
}

// These date fields (availableDate/dueDate/scheduledStart/scheduledEnd) are
// date-only concepts stored as a midnight DATETIME. Parsing them with timezone
// math shifts midnight across the day boundary (e.g. Jun 4 -> Jun 3 in US zones).
// Take only the calendar-date portion and parse with no zone conversion.
export function parseDateOnly(value) {
  if (!value) return null;
  const str = typeof value === 'string' ? value : moment(value).format('YYYY-MM-DD');
  const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
  return moment(match ? match[1] : str, 'YYYY-MM-DD');
}

/**
 * Gets the appropriate dates and labels for display based on user type, job state, and available data
 * @param {Object} request - The request object with availableDate, dueDate, currentState, etc.
 * @param {Array} calendarDays - Array of calendar entries for this request
 * @param {string} userType - 'dealer' or 'installer'
 * @returns {Object} { startDate, endDate, startLabel, endLabel, notification }
 */
// Calendar entries come from multiple endpoints — some use camelCase
// (`requestId`), some snake_case (`request_id`). Match either to keep the
// top "Scheduled Start/Complete" cards and the bottom dealer notification
// reading from the same set of days.
function filterCalendarDaysForRequest(calendarDays, requestId) {
  return calendarDays.filter(day =>
    day.requestId === requestId || day.request_id === requestId
  );
}

export function getDisplayDates(request, calendarDays, userType) {
  // Filter calendar days for this specific request
  const jobCalendarDays = filterCalendarDaysForRequest(calendarDays, request.requestId);

  // Get calendar dates if they exist
  const calendarDates = getCalendarDateRange(jobCalendarDays);
  
  // Determine if job is scheduled. Gate on state, not on scheduledStart presence
  // (request_offered holds a proposed date even before booking).
  const isScheduled = isJobScheduled(request);

  if (userType === 'dealer') {
    return getDealerDisplay(request, calendarDates, isScheduled);
  } else {
    return getInstallerDisplay(request, calendarDates, isScheduled);
  }
}

/**
 * Dealer sees scheduled dates for jobs, original dates for estimates
 */
function getDealerDisplay(request, calendarDates, isScheduled) {
  if (isScheduled) {
    // Job is booked - show scheduled work dates from request_offered
    return {
      startDate: request.scheduledStart || calendarDates.startDate || request.availableDate,
      endDate: request.scheduledEnd || calendarDates.endDate || request.dueDate,
      startLabel: 'Scheduled Start',
      endLabel: 'Scheduled Complete',
      notification: null
    };
  }
  // Estimate or unbooked - show original request dates
  return {
    startDate: request.availableDate,
    endDate: request.dueDate,
    startLabel: 'Available',
    endLabel: 'Due',
    notification: null
  };
}

/**
 * Installer sees scheduled dates from request_offered, original timeline for new requests
 */
function getInstallerDisplay(request, calendarDates, isScheduled) {
  // For finished jobs (pickups), show when work was completed
  if (request.currentState === 'finished') {
    const completionDate = request.scheduledEnd || calendarDates.endDate || request.dueDate;
    return {
      startDate: completionDate,          // Show completion date
      endDate: null,                      // No second date needed
      startLabel: 'Ready for Pickup',
      endLabel: null,
      notification: null
    };
  }

  if (isScheduled) {
    // Use scheduled dates from request_offered
    return {
      startDate: request.scheduledStart || calendarDates.startDate || request.availableDate,
      endDate: request.scheduledEnd || calendarDates.endDate || request.dueDate,
      startLabel: 'Start',
      endLabel: 'Complete',
      notification: null
    };
  } else {
    // Use original timeline for new/unbooked requests
    const isRetailJob = request.requestType === 'installer';
    return {
      startDate: request.availableDate,
      endDate: request.dueDate,
      startLabel: isRetailJob ? 'Offered' : 'Available',
      endLabel: isRetailJob ? 'Complete By' : 'Due',
      notification: null
    };
  }
}

/**
 * Gets schedule change notification for dealer detail view
 * @param {Object} request - The request object
 * @param {Array} calendarDays - Calendar entries for this request
 * @returns {Object|null} Notification object or null
 */
export function getScheduleNotification(request, calendarDays) {
  // Only surface scheduling info once the job is actually scheduled. Until then
  // request_offered holds a *proposed* date that must not be shown as scheduled.
  if (!isJobScheduled(request)) {
    return null;
  }

  // Match the same filter casing that getDisplayDates uses so the top cards and
  // this notification stay in sync.
  const jobCalendarDays = filterCalendarDaysForRequest(calendarDays, request.requestId);
  const calendarDates = getCalendarDateRange(jobCalendarDays);

  // Prefer the same priority chain the top "Scheduled Start/Complete" cards
  // use: request_offered JOIN fields first, calendar-derived as fallback.
  // Otherwise the two surfaces can disagree when those sources diverge.
  const startSource = request.scheduledStart || calendarDates.startDate;
  const endSource = request.scheduledEnd || calendarDates.endDate;

  if (!startSource || !endSource) {
    return null;
  }

  // Date-only parse — these are calendar dates, not timestamps; avoid TZ shift.
  const originalStart = parseDateOnly(request.availableDate);
  const originalDue = parseDateOnly(request.dueDate);
  const scheduledStart = parseDateOnly(startSource);
  const scheduledEnd = parseDateOnly(endSource);

  // The "after required due date" warning only makes sense when the dealer
  // supplied the due date. Installer-entered jobs (walkInForm sets
  // createdType='installer' and uses availableDate as the due date) have
  // no dealer-supplied deadline to violate — suppress the warning for them.
  const installerEntered = request.createdType === 'installer';

  // Check if due date is at risk (scheduled end > due date)
  if (!installerEntered && scheduledEnd.isAfter(originalDue)) {
    return {
      type: 'warning',
      message: `Scheduled to finish ${scheduledEnd.format('MMM DD')} - after required due date`
    };
  }

  // Check if start date moved significantly (≥2 days)
  const startDaysDiff = Math.abs(scheduledStart.diff(originalStart, 'days'));
  if (startDaysDiff >= 2) {
    return {
      type: 'info',
      message: `Rescheduled to start ${scheduledStart.format('MMM DD')} (completing by ${originalDue.format('MMM DD')})`
    };
  }

  return {
      type: 'info',
      message: `Specialist Start Date : ${scheduledStart.format('MMM DD')} - End Date : ${scheduledEnd.format('MMM DD')}`
    };
}

function getCalendarDateRange(calendarDays) {
  const activeEntries = calendarDays.filter(day =>
    ['booked', 'pending', 'started', 'finished', 'completed'].includes(day.workStatus)
  );

  if (activeEntries.length === 0) return { startDate: null, endDate: null };

  let jobStartDate = null;
  let jobEndDate = null;

  activeEntries.forEach(entry => {
    // Extract just the date portion (YYYY-MM-DD) to avoid timezone conversion issues
    if (!entry.workDate) return;
    const dateOnly = entry.workDate.substring(0, 10);
    const serviceStart = moment(dateOnly);

    // Whole calendar days, ceiling — the same rule the booking path and the
    // calendar use (util/durationSpan.js). This previously added the raw
    // decimal, and moment ROUNDS a fractional day: a 1.2-day job displayed one
    // day while it booked two. 74 production requests were affected.
    const serviceEnd = serviceStart
      .clone()
      .add(calendarDaySpan(entry.duration) - 1, 'days');

    // Track earliest start
    if (!jobStartDate || serviceStart.isBefore(jobStartDate)) {
      jobStartDate = serviceStart;
    }

    // Track latest completion
    if (!jobEndDate || serviceEnd.isAfter(jobEndDate)) {
      jobEndDate = serviceEnd;
    }
  });

  return {
    startDate: jobStartDate ? jobStartDate.format('YYYY-MM-DD') : null,
    endDate: jobEndDate ? jobEndDate.format('YYYY-MM-DD') : null
  };
}

/**
 * Helper function to format dates consistently
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDisplayDate(date) {
  if (!date) return '';
  return moment(date).format('MMM DD, YYYY');
}

/**
 * Helper function to check if a date is valid
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is valid
 */
export function isValidDate(date) {
  return date && moment(date).isValid();
}